import React, { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { uploadAndScanMenu, saveMenuFromScan, selectMenuScanResult, selectScanId } from './slice';
import { selectCurrentRestaurant } from '../Restaurant/slice';
import { useNavigate, useParams } from 'react-router-dom';
import { MenuSaveRequestDTO } from './scan-types';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import {
  WizardContainer,
  StepperContainer,
  StepItem,
  StepNumber,
  StepLabel,
  WizardCard,
  CardTitle,
  CardDescription,
  FormGrid,
  FormField,
  FormFieldFull,
  Label,
  DropZone,
  DropZoneText,
  DropZoneSubtext,
  FileInfo,
  FileInfoText,
  PreviewContainer,
  PreviewCard,
  PreviewImage,
  PreviewPlaceholder,
  EditableSection,
  SectionHeader,
  SectionName,
  DishList,
  DishItem,
  DishInputRow,
  InputSmall,
  TextAreaSmall,
  DishActions,
  AddButton,
  ConfirmSummary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ActionButtons,
  LoadingText
} from './MenuScanWizard.styled';

export interface MenuSectionData {
  name: string;
  dishes: DishData[];
}

export interface DishData {
  id: string;
  name: string;
  description: string;
  price: string;
}

export interface MenuScanState {
  restaurant: string;
  menuName: string;
  menuType: string;
  tags: string[];
  file: File | null;
  filePreviewUrl: string | null;
  sections: MenuSectionData[];
  scanId?: string;
}

interface MenuScanWizardProps {
  restaurants: Array<{ restaurantId: string; name: string }>;
  restaurantId?: string;
  restaurantName?: string;
  restaurantAlias?: string;
  onCancel: () => void;
  onSubmit: (data: MenuScanState) => Promise<void>;
}

const STEPS = [
  { label: 'Details', number: 1 },
  { label: 'Upload', number: 2 },
  { label: 'Review & Edit', number: 3 },
  { label: 'Confirm', number: 4 }
];

const MenuScanWizard: React.FC<MenuScanWizardProps> = ({
  restaurants,
  restaurantId,
  restaurantName,
  restaurantAlias,
  onCancel,
  onSubmit
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ restaurantId?: string; alias?: string }>();
  const routeRestaurantId = params.restaurantId;
  const routeAlias = params.alias;
  
  // Read from Redux for the review step
  const scanId = useAppSelector(selectScanId);
  const scanResult = useAppSelector(selectMenuScanResult);
  const currentRestaurant = useAppSelector(selectCurrentRestaurant);
  const finalRestaurantId = restaurantId ?? routeRestaurantId ?? scanResult?.restaurantId ?? '';
  
  // Get restaurant alias: prefer prop, then route param, then Redux, then fallback to empty
  const finalRestaurantAlias = restaurantAlias ?? routeAlias ?? currentRestaurant?.alias ?? '';

  const [formData, setFormData] = useState<MenuScanState>({
    restaurant: restaurantId ?? '',
    menuName: '',
    menuType: '',
    tags: [],
    file: null,
    filePreviewUrl: null,
    sections: []
  });
  
  // Boolean used in JSX for conditional rendering and disabling buttons
  // Use wizard's sections state (derived from result.dishes after grouping)
  const hasDishes = formData.sections.some(section => section.dishes.length > 0);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  // eslint-disable-next-line
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingMenu, setIsSavingMenu] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Validation errors
  const [errors, setErrors] = useState<{
    restaurantId?: string;
    menuName?: string;
    file?: string;
    sections?: string;
    dishes?: Record<string, Record<string, string>>; // sectionIndex -> dishIndex -> error
  }>({});

  const [tagInput, setTagInput] = useState('');

  const validateStep1 = (): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;

    if (!formData.restaurant) {
      newErrors.restaurantId = 'Restaurant is required';
      isValid = false;
    }
    if (!formData.menuName || formData.menuName.trim() === '') {
      newErrors.menuName = 'Menu name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep2 = (): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;

    if (!formData.file) {
      newErrors.file = 'Please select a menu image or PDF before scanning.';
      isValid = false;
    } else {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(formData.file.type)) {
        newErrors.file = 'File must be a JPG, PNG, or PDF';
        isValid = false;
      }
      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (formData.file.size > maxSize) {
        newErrors.file = 'File size must be less than 10MB';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep3 = (): boolean => {
    const newErrors: typeof errors = {};
    let isValid = true;
    const dishErrors: Record<string, Record<string, string>> = {};

    // Check if there's at least one section with at least one dish
    const hasValidDish = formData.sections.some(section => 
      section.dishes.length > 0 && section.dishes.some(dish => dish.name.trim() !== '')
    );

    if (!hasValidDish) {
      newErrors.sections = 'You must have at least one dish to save this menu.';
      isValid = false;
    }

    // Validate each dish name
    formData.sections.forEach((section, sectionIndex) => {
      section.dishes.forEach((dish, dishIndex) => {
        if (dish.name.trim() === '') {
          if (!dishErrors[sectionIndex]) {
            dishErrors[sectionIndex] = {};
          }
          dishErrors[sectionIndex][dishIndex] = 'Dish name is required';
          isValid = false;
        }
      });
    });

    if (Object.keys(dishErrors).length > 0) {
      newErrors.dishes = dishErrors;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = async (): Promise<void> => {
    // Validate before proceeding
    if (currentStep === 1) {
      if (!validateStep1()) {
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!validateStep2()) {
        return;
      }
      await handleUploadAndScan();
    } else if (currentStep === 3) {
      if (!validateStep3()) {
        return;
      }
      setCurrentStep(4);
    } else if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUploadAndScan = async (): Promise<void> => {
    if (!formData.file || !formData.restaurant || !formData.menuName) {
      setScanError('Please fill in all required fields');
      return;
    }

    setIsScanning(true);
    setScanError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      formDataToSend.append('restaurantId', formData.restaurant);
      formDataToSend.append('menuName', formData.menuName);
      if (formData.menuType) {
        formDataToSend.append('menuType', formData.menuType);
      }

      const result = await dispatch(uploadAndScanMenu(formDataToSend)).unwrap();

      // Normalize dishes from result (backend returns flat `dishes` array)
      const apiDishes = result.dishes ?? [];

      // Check if any dishes were found
      const hasDishes = apiDishes.length > 0;

      if (!hasDishes) {
        setIsScanning(false);
        setScanError(
          'No dishes were found in the uploaded menu. Please try with a clearer image or different menu.'
        );
        return;
      }

      // Group dishes by section to build MenuSectionData[]
      // If `dish.section` is null/empty, we default to "Menu"
      const sectionMap = new Map<string, DishData[]>();

      apiDishes.forEach((dish, dishIdx) => {
        const sectionName = dish.section?.trim() ?? 'Menu';

        if (!sectionMap.has(sectionName)) {
          sectionMap.set(sectionName, []);
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const sectionDishes = sectionMap.get(sectionName)!;

        sectionDishes.push({
          id: `dish_${sectionName}_${dishIdx}_${Date.now()}`,
          name: dish.name ?? '',
          description: dish.description ?? '',
          price: dish.price != null ? String(dish.price) : ''
        });
      });

      // Convert map to array for wizard state
      const sections: MenuSectionData[] = Array.from(sectionMap.entries()).map(
        ([name, dishes]) => ({
          name,
          dishes
        })
      );

      setFormData(prev => ({ ...prev, sections, scanId: result.scanId }));
      setIsScanning(false);
      setErrors({}); // Clear errors on success
      setCurrentStep(3);
    } catch (error: any) {
      setIsScanning(false);
      setScanError(error?.message ?? 'Failed to scan menu. Please try again.');
      console.error('Upload and scan error:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Clear previous file errors
    setErrors(prev => ({ ...prev, file: undefined }));
    
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          file,
          filePreviewUrl: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setErrors(prev => ({ ...prev, file: 'File must be a JPG, PNG, or PDF' }));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleAddSection = () => {
    const newSection: MenuSectionData = {
      name: `Section ${formData.sections.length + 1}`,
      dishes: []
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const handleUpdateSectionName = (index: number, name: string) => {
    setFormData(prev => {
      const updatedSections = [...prev.sections];
      updatedSections[index].name = name;
      return { ...prev, sections: updatedSections };
    });
  };

  const handleAddDish = (sectionIndex: number) => {
    setFormData(prev => {
      const updatedSections = [...prev.sections];
      updatedSections[sectionIndex].dishes.push({
        id: `dish_${Date.now()}`,
        name: '',
        description: '',
        price: ''
      });
      return { ...prev, sections: updatedSections };
    });
  };

  const handleUpdateDish = (sectionIndex: number, dishIndex: number, field: keyof DishData, value: string) => {
    setFormData(prev => {
      const updatedSections = [...prev.sections];
      updatedSections[sectionIndex].dishes[dishIndex][field] = value;
      return { ...prev, sections: updatedSections };
    });
  };

  const handleRemoveDish = (sectionIndex: number, dishIndex: number) => {
    setFormData(prev => {
      const updatedSections = [...prev.sections];
      updatedSections[sectionIndex].dishes.splice(dishIndex, 1);
      return { ...prev, sections: updatedSections };
    });
  };

  const handleRemoveSection = (sectionIndex: number) => {
    setFormData(prev => {
      const updatedSections = [...prev.sections];
      updatedSections.splice(sectionIndex, 1);
      return { ...prev, sections: updatedSections };
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSaveMenu = async (): Promise<void> => {
    const restaurantIdToUse = finalRestaurantId;
    const scanIdToUse = scanId ?? formData.scanId;
    
    if (!restaurantIdToUse || !scanIdToUse) {
      console.error('Missing restaurantId or scanId for saveMenuFromScan', {
        restaurantId: restaurantIdToUse,
        scanId: scanIdToUse
      });
      return;
    }

    if (!formData.menuName.trim()) {
      // Show validation error
      setErrors(prev => ({ ...prev, menuName: 'Menu name is required' }));
      return;
    }

    const request: MenuSaveRequestDTO = {
      restaurantId: restaurantIdToUse,
      menuName: formData.menuName.trim(),
      // NOTE: we can omit dishes and let backend use stored scan result.
      // If later we support editing dishes, we can send them here:
      // dishes,
    };

    try {
      setIsSavingMenu(true);
      const response = await dispatch(
        saveMenuFromScan({ scanId: scanIdToUse, request })
      ).unwrap();

      const menuId = response.menuId;

      // Navigate to the menu detail page
      // Route format: /restaurant/:alias/menu/:menuID
      // IMPORTANT: Use alias, not restaurantId (UUID)
      if (menuId && finalRestaurantAlias) {
        console.log('Navigating to menu page', {
          alias: finalRestaurantAlias,
          menuId,
          restaurantId: restaurantIdToUse
        });
        navigate(`/restaurant/${finalRestaurantAlias}/menu/${menuId}`);
      } else if (finalRestaurantAlias) {
        console.warn('Menu saved but menuId not available, navigating to restaurant page', {
          alias: finalRestaurantAlias,
          response
        });
        navigate(`/restaurant/${finalRestaurantAlias}`);
      } else {
        console.error('Menu saved but alias not available for navigation', {
          restaurantId: restaurantIdToUse,
          menuId,
          alias: finalRestaurantAlias,
          routeAlias,
          restaurantAlias,
          currentRestaurantAlias: currentRestaurant?.alias
        });
        // Last resort: navigate to dashboard
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to save menu from scan', error);
      // Error is handled via slice (status/message) or you can add a local snackbar
    } finally {
      setIsSavingMenu(false);
    }
  };

  return (
    <WizardContainer>
      {/* Stepper */}
      <StepperContainer>
        {STEPS.map((step, index) => (
          <StepItem
            key={step.number}
            isActive={currentStep === step.number}
            isCompleted={currentStep > step.number}
          >
            <StepNumber isActive={currentStep === step.number} isCompleted={currentStep > step.number}>
              {currentStep > step.number ? '✓' : step.number}
            </StepNumber>
            <StepLabel isActive={currentStep === step.number}>{step.label}</StepLabel>
          </StepItem>
        ))}
      </StepperContainer>

      {/* Step 1: Details */}
      {currentStep === 1 && (
        <WizardCard>
          <CardTitle>Menu Details</CardTitle>
          <CardDescription>Select a restaurant and provide basic menu information</CardDescription>

          <FormGrid>
            {restaurantId != null && restaurantName != null ? (
              <FormField>
                <Label>Restaurant</Label>
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #d4dce8',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: '#0a1e3d',
                  fontWeight: 600
                }}>
                  {restaurantName}
                </div>
              </FormField>
            ) : (
              <FormField>
                <Label>Restaurant *</Label>
                <FormControl fullWidth size="small" error={!!errors.restaurantId} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fff', '& fieldset': { borderColor: '#d4dce8' }, '&:hover fieldset': { borderColor: '#ff8c42' }, '&.Mui-focused fieldset': { borderColor: '#ff8c42', boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.1)' } }, '& .MuiInputLabel-root': { color: '#666', fontSize: '13px', '&.Mui-focused': { color: '#ff8c42' } } }}>
                  <InputLabel>Select Restaurant</InputLabel>
                  <Select
                    value={formData.restaurant}
                    label="Select Restaurant"
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, restaurant: e.target.value }));
                      setErrors(prev => ({ ...prev, restaurantId: undefined }));
                    }}
                  >
                    {restaurants.map(r => (
                      <MenuItem key={r.restaurantId} value={r.restaurantId}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.restaurantId && <FormHelperText>{errors.restaurantId}</FormHelperText>}
                </FormControl>
              </FormField>
            )}

            <FormField>
              <Label>Menu Name *</Label>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Dinner Menu"
                value={formData.menuName}
                error={!!errors.menuName}
                helperText={errors.menuName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, menuName: e.target.value }));
                  setErrors(prev => ({ ...prev, menuName: undefined }));
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fff', '& fieldset': { borderColor: '#d4dce8' }, '&:hover fieldset': { borderColor: '#ff8c42' }, '&.Mui-focused fieldset': { borderColor: '#ff8c42', boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.1)' } }, '& .MuiInputLabel-root': { color: '#666', fontSize: '13px', '&.Mui-focused': { color: '#ff8c42' } } }}
              />
            </FormField>

            <FormField>
              <Label>Menu Type (Optional)</Label>
              <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fff', '& fieldset': { borderColor: '#d4dce8' }, '&:hover fieldset': { borderColor: '#ff8c42' }, '&.Mui-focused fieldset': { borderColor: '#ff8c42', boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.1)' } }, '& .MuiInputLabel-root': { color: '#666', fontSize: '13px', '&.Mui-focused': { color: '#ff8c42' } } }}>
                <InputLabel>Menu Type</InputLabel>
                <Select
                  value={formData.menuType}
                  label="Menu Type"
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, menuType: e.target.value }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="brunch">Brunch</MenuItem>
                  <MenuItem value="lunch">Lunch</MenuItem>
                  <MenuItem value="dinner">Dinner</MenuItem>
                  <MenuItem value="dessert">Dessert</MenuItem>
                  <MenuItem value="drinks">Drinks</MenuItem>
                </Select>
              </FormControl>
            </FormField>

            <FormFieldFull>
              <Label>Tags (Optional)</Label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add tags..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fff', '& fieldset': { borderColor: '#d4dce8' }, '&:hover fieldset': { borderColor: '#ff8c42' }, '&.Mui-focused fieldset': { borderColor: '#ff8c42', boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.1)' } } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddTag}
                  sx={{ backgroundColor: '#ff8c42', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#ff7a2f' } }}
                >
                  Add
                </Button>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                {formData.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                    sx={{ backgroundColor: '#fff4e6', color: '#ff8c42', fontWeight: 600 }}
                  />
                ))}
              </div>
            </FormFieldFull>
          </FormGrid>

          <ActionButtons>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{ color: '#0a1e3d', borderColor: '#d4dce8', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#0a1e3d' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void handleNext();
              }}
              sx={{ backgroundColor: '#ff8c42', color: '#fff', '&:hover': { backgroundColor: '#ff7a2f' } }}
            >
              Next
            </Button>
          </ActionButtons>
        </WizardCard>
      )}

      {/* Step 2: Upload */}
      {currentStep === 2 && (
        <WizardCard>
          <CardTitle>Upload Menu</CardTitle>
          <CardDescription>
            Upload a clear photo or PDF of the menu. We will scan it and extract dishes.
          </CardDescription>

          {isScanning && (
            <div style={{ marginBottom: '16px' }}>
              <LinearProgress sx={{ height: 4, borderRadius: '2px', backgroundColor: '#e8eef5', '& .MuiLinearProgress-bar': { backgroundColor: '#ff8c42' } }} />
              <LoadingText style={{ marginTop: '12px', color: '#ff8c42', fontWeight: 600 }}>Scanning menu and extracting dishes...</LoadingText>
            </div>
          )}

          {scanError && (
            <div style={{ padding: '16px', backgroundColor: '#fce4ec', borderLeft: '4px solid #d32f2f', borderRadius: '8px', color: '#c62828', marginBottom: '16px', fontWeight: 500 }}>
              {scanError}
            </div>
          )}

          {errors.file && (
            <div style={{ padding: '16px', backgroundColor: '#fce4ec', borderLeft: '4px solid #d32f2f', borderRadius: '8px', color: '#c62828', marginBottom: '16px', fontWeight: 500 }}>
              {errors.file}
            </div>
          )}

          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <DropZone isDragOver={isDragOver}>
              <DropZoneText>📁 Drag and drop your menu image or PDF here</DropZoneText>
              <DropZoneSubtext>or</DropZoneSubtext>
              <Button
                variant="contained"
                component="label"
                size="small"
                disabled={isScanning}
                sx={{ backgroundColor: '#ff8c42', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#ff7a2f' } }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handleFileInputChange}
                />
              </Button>
              <DropZoneSubtext>JPG, PNG, or PDF • Max 10MB</DropZoneSubtext>
            </DropZone>
          </div>

          {formData.file && (
            <FileInfo>
              <FileInfoText>
                ✓ {formData.file.name} ({(formData.file.size / 1024).toFixed(2)} KB)
              </FileInfoText>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    file: null,
                    filePreviewUrl: null
                  }));
                  setErrors(prev => ({ ...prev, file: undefined }));
                }}
                disabled={isScanning}
                sx={{ color: '#0a1e3d', '&:hover': { backgroundColor: 'transparent', color: '#ff8c42' } }}
              >
                Change
              </Button>
            </FileInfo>
          )}

          <ActionButtons>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isScanning}
              sx={{ color: '#0a1e3d', borderColor: '#d4dce8', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#0a1e3d' } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void handleNext();
              }}
              disabled={!formData.file || isScanning}
              sx={{ backgroundColor: '#ff8c42', color: '#fff', '&:hover': { backgroundColor: '#ff7a2f' }, '&:disabled': { backgroundColor: '#e0e0e0', color: '#999' } }}
            >
              {isScanning ? 'Scanning...' : 'Upload & Scan'}
            </Button>
          </ActionButtons>
        </WizardCard>
      )}

      {/* Step 3: Review & Edit */}
      {currentStep === 3 && (
        <WizardCard>
          <CardTitle>Review & Edit Menu</CardTitle>
          <CardDescription>Review the extracted data and make any corrections</CardDescription>

          {!hasDishes ? (
            <div style={{ 
              padding: '24px', 
              backgroundColor: '#fff4e6', 
              borderLeft: '4px solid #ff8c42', 
              borderRadius: '8px', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <Typography variant="body1" sx={{ color: '#0a1e3d', fontWeight: 600, marginBottom: '8px' }}>
                No dishes were detected in this scan.
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', marginBottom: '16px' }}>
                Try uploading a clearer menu image or a different page.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ 
                  color: '#0a1e3d', 
                  borderColor: '#d4dce8', 
                  '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#0a1e3d' } 
                }}
              >
                Go Back & Try Different File
              </Button>
            </div>
          ) : (
            <PreviewContainer>
            {/* Left: Image Preview */}
            <PreviewCard>
              <CardTitle style={{ fontSize: '14px', marginBottom: '8px' }}>
                Original Menu
              </CardTitle>
              {formData.filePreviewUrl ? (
                <PreviewImage
                  src={formData.filePreviewUrl}
                  alt="Menu preview"
                />
              ) : (
                <PreviewPlaceholder>No image preview</PreviewPlaceholder>
              )}
            </PreviewCard>

            {/* Right: Editable Data */}
            <PreviewCard>
              <CardTitle style={{ fontSize: '14px', marginBottom: '8px' }}>
                Extracted Data
              </CardTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {formData.sections.map((section, sectionIndex) => (
                  <EditableSection key={sectionIndex}>
                    <SectionHeader>
                      <SectionName
                        value={section.name}
                        onChange={(e) =>
                          handleUpdateSectionName(sectionIndex, e.target.value)
                        }
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveSection(sectionIndex)}
                        sx={{ color: '#d32f2f', borderColor: '#f48fb1', '&:hover': { backgroundColor: '#fce4ec' } }}
                      >
                        Remove Section
                      </Button>
                    </SectionHeader>

                    <DishList>
                      {section.dishes.map((dish, dishIndex) => {
                        const dishError = errors.dishes?.[sectionIndex]?.[dishIndex];
                        return (
                          <DishItem key={dish.id}>
                            <DishInputRow>
                              <div style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
                                <InputSmall
                                  placeholder="Dish name *"
                                  value={dish.name}
                                  style={{
                                    borderColor: dishError ? '#d32f2f' : undefined
                                  }}
                                  onChange={(e) => {
                                    handleUpdateDish(
                                      sectionIndex,
                                      dishIndex,
                                      'name',
                                      e.target.value
                                    );
                                    // Clear error when user types
                                    if (dishError) {
                                      setErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.dishes?.[sectionIndex]?.[dishIndex]) {
                                          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                                          delete newErrors.dishes[sectionIndex][dishIndex];
                                          if (Object.keys(newErrors.dishes[sectionIndex]).length === 0) {
                                            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                                            delete newErrors.dishes[sectionIndex];
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                  }}
                                />
                                {dishError && (
                                  <span style={{ fontSize: '12px', color: '#d32f2f', marginTop: '4px' }}>
                                    {dishError}
                                  </span>
                                )}
                              </div>
                              <InputSmall
                                placeholder="Price"
                                value={dish.price}
                                onChange={(e) =>
                                  handleUpdateDish(
                                    sectionIndex,
                                    dishIndex,
                                    'price',
                                    e.target.value
                                  )
                                }
                              />
                              <DishActions>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() =>
                                    handleRemoveDish(sectionIndex, dishIndex)
                                  }
                                  sx={{ color: '#d32f2f', borderColor: '#f48fb1', '&:hover': { backgroundColor: '#fce4ec' } }}
                                >
                                  Remove
                                </Button>
                              </DishActions>
                            </DishInputRow>
                            <TextAreaSmall
                              placeholder="Description"
                              value={dish.description}
                              onChange={(e) =>
                                handleUpdateDish(
                                  sectionIndex,
                                  dishIndex,
                                  'description',
                                  e.target.value
                                )
                              }
                            />
                          </DishItem>
                        );
                      })}
                    </DishList>

                    <AddButton onClick={() => handleAddDish(sectionIndex)}>
                      + Add Dish
                    </AddButton>
                  </EditableSection>
                ))}

                <AddButton onClick={handleAddSection}>
                  + Add Section
                </AddButton>
              </div>
            </PreviewCard>
          </PreviewContainer>
          )}

          {errors.sections && (
            <div style={{ padding: '16px', backgroundColor: '#fce4ec', borderLeft: '4px solid #d32f2f', borderRadius: '8px', color: '#c62828', marginBottom: '16px', fontWeight: 500 }}>
              {errors.sections}
            </div>
          )}

          <ActionButtons>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ color: '#0a1e3d', borderColor: '#d4dce8', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#0a1e3d' } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void handleNext();
              }}
              sx={{ backgroundColor: '#ff8c42', color: '#fff', '&:hover': { backgroundColor: '#ff7a2f' } }}
            >
              Next
            </Button>
          </ActionButtons>
        </WizardCard>
      )}

      {/* Step 4: Confirm */}
      {currentStep === 4 && (
        <WizardCard>
          <CardTitle>Confirm & Save</CardTitle>
          <CardDescription>Review your menu before saving</CardDescription>

          <ConfirmSummary>
            <SummaryItem>
              <SummaryLabel>Restaurant</SummaryLabel>
              <SummaryValue>
                {restaurants.find(r => r.restaurantId === formData.restaurant)?.name ?? '-'}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Menu Name</SummaryLabel>
              <SummaryValue>{formData.menuName}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Menu Type</SummaryLabel>
              <SummaryValue>{formData.menuType || 'Not specified'}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>Sections & Dishes</SummaryLabel>
              <SummaryValue>
                {formData.sections.length} sections,{' '}
                {formData.sections.reduce((total, s) => total + s.dishes.length, 0)} dishes
              </SummaryValue>
            </SummaryItem>
          </ConfirmSummary>

          <ActionButtons>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={isSubmitting}
              sx={{ color: '#0a1e3d', borderColor: '#d4dce8', '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#0a1e3d' } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void handleSaveMenu();
              }}
              disabled={isSavingMenu || !formData.menuName.trim() || !hasDishes}
              startIcon={isSavingMenu ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
              sx={{ backgroundColor: '#4caf50', color: '#fff', '&:hover': { backgroundColor: '#45a049' }, '&:disabled': { backgroundColor: '#e0e0e0', color: '#999' } }}
            >
              {isSavingMenu ? 'Saving…' : 'Save menu'}
            </Button>
          </ActionButtons>
        </WizardCard>
      )}
    </WizardContainer>
  );
};

export default MenuScanWizard;
